import React, { memo, useCallback } from "react";
import { Box, Grid, Typography } from "@material-ui/core";
import IconButton from "uiKit/buttons/IconButton";
import DeleteOutlineIcon from "@material-ui/icons/DeleteOutline";
import { useNotificationItemStyles } from "components/notifications/notificationItem/styles";
import NotificationEnvelopeClosed from "uiKit/icons/NotificationEnvelopeClosed";
import NotificationEnvelopeOpened from "uiKit/icons/NotificationEnvelopeOpened";
import TextButton from "uiKit/buttons/TextButton";
import Tooltip from "uiKit/container/tooltip";
import { useLocalize } from "shared/hooks/useLocalize";
import { LocalizationId } from "components/notifications/notificationsButton/localization";
import ReactMarkdown from "react-markdown";
import Link from "uiKit/Link";
import { ReactMarkdownProps } from "react-markdown/src/ast-to-react";
import { openUrlInNewTab } from "shared/helpers/openInNewTab";
import clsx from "clsx";
import { useRequestContext } from "shared/api/RequestProvider";
import { DELETE_ALL_NOTIFICATIONS, DELETE_NOTIFICATION } from "shared/constants/requestActionConstants";

interface NotificationItemProps {
    index: number;
    isRead: boolean;
    title?: string;
    onDeleteNotification: (index: number) => Promise<void>;
    created: string;
    description?: string;
    links?: Array<{ text: string; url: string }>;
    style?: React.CSSProperties;
}

const NotificationItem = ({
    index,
    title,
    isRead,
    description,
    created,
    links = [],
    style,
    onDeleteNotification,
}: NotificationItemProps): JSX.Element => {
    const notificationItemClasses = useNotificationItemStyles();
    const localize = useLocalize();
    const { executeRequest, isLoading, actionName } = useRequestContext();

    const getThemeLink = useCallback(
        ({
            children,
            href,
        }: React.ClassAttributes<HTMLAnchorElement> &
            React.AnchorHTMLAttributes<HTMLAnchorElement> &
            ReactMarkdownProps) => {
            return <Link label={children[0]} link={href} isOpenAtNewPage typographyVariant="body2" />;
        },
        []
    );

    const handleDeleteNotification = useCallback(async () => {
        await executeRequest(onDeleteNotification, DELETE_NOTIFICATION(index), [index]);
    }, [executeRequest, onDeleteNotification, index]);

    const isDeleteActionDisabled =
        isLoading && (actionName === DELETE_NOTIFICATION(index) || actionName === DELETE_ALL_NOTIFICATIONS);

    return (
        <Grid
            container
            direction="row"
            className={clsx(notificationItemClasses.root, "notificationItem")}
            style={style}
        >
            <Grid container item>
                <Grid container item>
                    <Box className={notificationItemClasses.icon}>
                        {isRead ? <NotificationEnvelopeOpened /> : <NotificationEnvelopeClosed />}
                    </Box>
                    <Typography variant="caption">{created}</Typography>
                </Grid>
                <Grid container direction="row" wrap="nowrap" justify="space-between">
                    <Grid item>
                        {title && (
                            <Grid item>
                                <Typography className={notificationItemClasses.title}>{title}</Typography>
                            </Grid>
                        )}
                        <Grid item>
                            <Typography variant="body2" className={notificationItemClasses.description}>
                                {description ? (
                                    <ReactMarkdown
                                        components={{
                                            a: getThemeLink,
                                        }}
                                    >
                                        {description}
                                    </ReactMarkdown>
                                ) : null}
                            </Typography>
                        </Grid>
                    </Grid>
                    <Grid item>
                        <Tooltip title={localize(LocalizationId.RemoveNotificationTooltip)}>
                            <span>
                                <IconButton
                                    icon={DeleteOutlineIcon}
                                    palette="primary"
                                    noShadow
                                    size="m"
                                    onClick={handleDeleteNotification}
                                    disabled={isDeleteActionDisabled}
                                />
                            </span>
                        </Tooltip>
                    </Grid>
                </Grid>
                <Grid item>
                    {links?.length
                        ? links.map(({ text, url }) => (
                              <TextButton variant="primary" label={text} onClick={() => openUrlInNewTab(url)} />
                          ))
                        : null}
                </Grid>
            </Grid>
        </Grid>
    );
};

export default memo(NotificationItem);
